import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { interviewService } from '@/services/interview.service'
import { useConfirm } from '@/hooks/useConfirm'

interface SlotGroup {
  date: string
  dateLabel: string
  slots: Array<{ interviewerId: string; interviewerName: string; scheduledAt: string; timeLabel: string }>
}

function groupSlotsByDate(slots: Array<{ interviewerId: string; interviewerName: string; scheduledAt: string }>): SlotGroup[] {
  const groups: Record<string, SlotGroup> = {}

  for (const slot of slots) {
    const date = new Date(slot.scheduledAt)
    const dateKey = date.toISOString().split('T')[0]

    if (!groups[dateKey]) {
      groups[dateKey] = {
        date: dateKey,
        dateLabel: date.toLocaleDateString('de-DE', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        }),
        slots: [],
      }
    }

    groups[dateKey].slots.push({
      ...slot,
      timeLabel: date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }),
    })
  }

  return Object.values(groups).sort((a, b) => a.date.localeCompare(b.date))
}

export function BookInterviewPage({ onBooked }: { onBooked?: () => void }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { confirm, modal: confirmModal } = useConfirm()
  const [selectedSlot, setSelectedSlot] = useState<{ interviewerId: string; scheduledAt: string; interviewerName: string } | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['available-slots'],
    queryFn: () => interviewService.getAvailableSlots(),
  })

  const bookMutation = useMutation({
    mutationFn: (slot: { interviewerId: string; scheduledAt: string }) =>
      interviewService.bookInterview(slot),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['applicant'] })
      queryClient.invalidateQueries({ queryKey: ['available-slots'] })
      onBooked?.()
    },
  })

  const handleSlotClick = async (slot: { interviewerId: string; scheduledAt: string; interviewerName: string }) => {
    const date = new Date(slot.scheduledAt)
    const dateStr = date.toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long' })
    const timeStr = date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })

    const confirmed = await confirm({
      title: 'Interview buchen',
      message: `Möchtest du das Interview am ${dateStr} um ${timeStr} Uhr mit ${slot.interviewerName} buchen?`,
      confirmLabel: 'Ja, buchen',
      cancelLabel: 'Abbrechen',
    })

    if (confirmed) {
      setSelectedSlot(slot)
      bookMutation.mutate({ interviewerId: slot.interviewerId, scheduledAt: slot.scheduledAt })
    }
  }

  if (isLoading) {
    return <div className="text-gray-600 text-center py-8">{t('common.loading')}</div>
  }

  const slotGroups = groupSlotsByDate(data?.slots || [])

  if (slotGroups.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-600">Derzeit sind keine Interview-Termine verfügbar.</p>
        <p className="text-sm text-gray-500 mt-2">Bitte versuche es später erneut.</p>
      </div>
    )
  }

  if (bookMutation.isSuccess) {
    return (
      <div className="text-center py-8">
        <div className="text-green-600 text-lg font-semibold mb-2">✓ Interview gebucht!</div>
        <p className="text-gray-600">
          Du erhältst eine Bestätigungs-Email mit dem Meeting-Link.
        </p>
      </div>
    )
  }

  return (
    <div>
      <p className="text-sm text-gray-600 mb-4">
        Wähle einen freien Termin für dein 15-Minuten Qualifikationsinterview:
      </p>

      {bookMutation.isError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-800">
            {bookMutation.error instanceof Error ? bookMutation.error.message : 'Buchung fehlgeschlagen. Bitte versuche einen anderen Termin.'}
          </p>
        </div>
      )}

      <div className="space-y-6 max-h-96 overflow-y-auto">
        {slotGroups.map(group => (
          <div key={group.date}>
            <h4 className="text-sm font-semibold text-gray-900 mb-2">{group.dateLabel}</h4>
            <div className="flex flex-wrap gap-2">
              {group.slots.map(slot => (
                <button
                  key={`${slot.interviewerId}-${slot.scheduledAt}`}
                  onClick={() => handleSlotClick(slot)}
                  disabled={bookMutation.isPending}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-blue-50 hover:border-blue-300 transition-colors disabled:opacity-50"
                  title={`Interviewer: ${slot.interviewerName}`}
                >
                  {slot.timeLabel}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {bookMutation.isPending && (
        <div className="mt-4 text-center text-gray-600">
          Interview wird gebucht...
        </div>
      )}

      {confirmModal}
    </div>
  )
}
